import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  RefreshCw,
  Search,
  X,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { EmptyState } from "@/components/ui/empty-state"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { api } from "@/lib/api-client"

type DocumentStatus = "indexed" | "failed" | "oversized_deferred" | "unknown"

type LibraryDocument = {
  filename: string
  status: DocumentStatus
  timestamp: string | null
  md5: string | null
  error_message: string | null
}

type LibraryDocumentsResponse = {
  index_id: string
  documents: LibraryDocument[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

type LibraryDocumentBrowserProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  libraryId: string | null
  libraryName: string | null
  onRetryFile?: (libraryId: string, filename: string) => void
}

const STATUS_ICONS: Record<DocumentStatus, React.ReactNode> = {
  indexed: <CheckCircle2 className="text-success size-4" />,
  failed: <AlertCircle className="text-destructive size-4" />,
  oversized_deferred: <Clock className="text-warning-ink size-4" />,
  unknown: <FileText className="text-muted-foreground size-4" />,
}

const STATUS_LABELS: Record<DocumentStatus, string> = {
  indexed: "Indexed",
  failed: "Failed",
  oversized_deferred: "Deferred",
  unknown: "Unknown",
}

function getStatusBadgeVariant(status: DocumentStatus): "default" | "destructive" | "secondary" {
  if (status === "indexed") return "default"
  if (status === "failed") return "destructive"
  return "secondary"
}

type GetLibraryDocumentsParams = {
  libraryId: string
  page: number
  pageSize: number
  statusFilter: string | null
  search: string
}

async function getLibraryDocuments({
  libraryId,
  page,
  pageSize,
  statusFilter,
  search,
}: GetLibraryDocumentsParams): Promise<LibraryDocumentsResponse> {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  })
  if (statusFilter && statusFilter !== "all") {
    params.set("status", statusFilter)
  }
  if (search.trim()) {
    params.set("search", search.trim())
  }

  const response = await api.get(`/admin/libraries/${libraryId}/documents?${params.toString()}`)
  return response.data || response
}

export function LibraryDocumentBrowser({
  isOpen,
  onOpenChange,
  libraryId,
  libraryName,
  onRetryFile,
}: LibraryDocumentBrowserProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    // Simple debounce using setTimeout
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(value)
      setPage(1) // Reset to first page on search
    }, 300)
    return () => clearTimeout(timeoutId)
  }

  const handleStatusChange = (value: string) => {
    setStatusFilter(value)
    setPage(1) // Reset to first page on filter change
  }

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "admin",
      "libraries",
      libraryId,
      "documents",
      page,
      pageSize,
      statusFilter,
      debouncedSearch,
    ],
    queryFn: () =>
      getLibraryDocuments({
        libraryId: libraryId!,
        page,
        pageSize,
        statusFilter,
        search: debouncedSearch,
      }),
    enabled: isOpen && !!libraryId,
    staleTime: 30000,
  })

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return "—"
    try {
      return new Date(timestamp).toLocaleString()
    } catch {
      return timestamp
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="size-5" />
            {t("adminDashboard.libraries.documentBrowser.title", "Document Browser")}
          </DialogTitle>
          <DialogDescription>
            {libraryName && (
              <span>
                {t(
                  "adminDashboard.libraries.documentBrowser.description",
                  "Viewing documents in {{name}}",
                  { name: libraryName }
                )}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                className="pr-9 pl-9"
                value={searchTerm}
                placeholder={t(
                  "adminDashboard.libraries.documentBrowser.searchPlaceholder",
                  "Search by filename..."
                )}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              {searchTerm && (
                <Button
                  className="absolute top-1/2 right-1 size-7 -translate-y-1/2"
                  size="icon"
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setSearchTerm("")
                    setDebouncedSearch("")
                  }}
                >
                  <X className="size-4" />
                </Button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("adminDashboard.libraries.documentBrowser.allStatuses", "All Statuses")}
                </SelectItem>
                <SelectItem value="indexed">
                  {t("adminDashboard.libraries.documentBrowser.indexed", "Indexed")}
                </SelectItem>
                <SelectItem value="failed">
                  {t("adminDashboard.libraries.documentBrowser.failed", "Failed")}
                </SelectItem>
                <SelectItem value="oversized_deferred">
                  {t("adminDashboard.libraries.documentBrowser.deferred", "Deferred")}
                </SelectItem>
              </SelectContent>
            </Select>
            <Button size="icon" type="button" variant="outline" onClick={() => refetch()}>
              <RefreshCw className="size-4" />
            </Button>
          </div>

          {/* Table */}
          <div className="max-h-[50vh] overflow-auto rounded-md border">
            {isLoading && (
              <div className="space-y-2 p-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            )}
            {!isLoading && isError && (
              <div className="p-8 text-center">
                <AlertCircle className="text-destructive mx-auto size-8" />
                <p className="text-muted-foreground mt-2 text-sm">
                  {error instanceof Error ? error.message : "Failed to load documents"}
                </p>
                <Button className="mt-4" variant="outline" onClick={() => refetch()}>
                  {t("adminDashboard.libraries.documentBrowser.retry", "Retry")}
                </Button>
              </div>
            )}
            {!isLoading && !isError && data?.documents.length === 0 && (
              <EmptyState
                icon={FileText}
                title={t("adminDashboard.libraries.documentBrowser.emptyTitle", "No Documents")}
                description={
                  debouncedSearch || statusFilter !== "all"
                    ? t(
                        "adminDashboard.libraries.documentBrowser.noMatchingDocuments",
                        "No documents match your search criteria"
                      )
                    : t(
                        "adminDashboard.libraries.documentBrowser.noDocuments",
                        "No documents in this library"
                      )
                }
              />
            )}
            {!isLoading && !isError && data && data.documents.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50%]">
                      {t("adminDashboard.libraries.documentBrowser.filename", "Filename")}
                    </TableHead>
                    <TableHead className="w-[15%]">
                      {t("adminDashboard.libraries.documentBrowser.status", "Status")}
                    </TableHead>
                    <TableHead className="w-[25%]">
                      {t("adminDashboard.libraries.documentBrowser.lastUpdated", "Last Updated")}
                    </TableHead>
                    <TableHead className="w-[10%] text-right">
                      {t("adminDashboard.libraries.documentBrowser.actions", "Actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.documents.map((doc) => (
                    <TableRow key={doc.filename}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="text-muted-foreground size-4 shrink-0" />
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="max-w-[400px] truncate">{doc.filename}</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[500px]" side="bottom">
                              <p className="break-all">{doc.filename}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {STATUS_ICONS[doc.status] || STATUS_ICONS.unknown}
                          <Badge variant={getStatusBadgeVariant(doc.status)}>
                            {STATUS_LABELS[doc.status] || doc.status}
                          </Badge>
                        </div>
                        {doc.error_message && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <p className="text-destructive mt-1 max-w-[150px] truncate text-xs">
                                {doc.error_message}
                              </p>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[300px]" side="bottom">
                              <p className="text-xs">{doc.error_message}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(doc.timestamp)}
                      </TableCell>
                      <TableCell className="text-right">
                        {doc.status === "failed" && onRetryFile && libraryId && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                type="button"
                                variant="ghost"
                                onClick={() => onRetryFile(libraryId, doc.filename)}
                              >
                                <RefreshCw className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t(
                                "adminDashboard.libraries.documentBrowser.retryFile",
                                "Retry indexing"
                              )}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          {data && data.total > 0 && (
            <div className="flex items-center justify-between text-sm">
              <p className="text-muted-foreground">
                {t(
                  "adminDashboard.libraries.documentBrowser.showing",
                  "Showing {{start}}-{{end}} of {{total}} documents",
                  {
                    start: (page - 1) * pageSize + 1,
                    end: Math.min(page * pageSize, data.total),
                    total: data.total,
                  }
                )}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  disabled={page <= 1}
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <span className="min-w-[80px] text-center">
                  {t(
                    "adminDashboard.libraries.documentBrowser.pageOf",
                    "Page {{page}} of {{total}}",
                    {
                      page,
                      total: totalPages,
                    }
                  )}
                </span>
                <Button
                  disabled={!data.has_more}
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
